/**
 * DeleteEltJobModal Component
 *
 * Confirmation modal for deleting an ELT job.
 */

import React, { useState } from 'react';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Alert } from '../../../components/ui/Alert';
import { useDeleteEltJob } from '../hooks/useDeleteEltJob';
import { useAuth } from '../../../contexts/AuthContext';
import type { EltJob } from '../../../types/elt-job.types';

interface DeleteEltJobModalProps {
  isOpen: boolean;
  onClose: () => void;
  eltJob: EltJob;
}

export const DeleteEltJobModal: React.FC<DeleteEltJobModalProps> = ({
  isOpen,
  onClose,
  eltJob,
}) => {
  const { tenant } = useAuth();
  const tenantId = tenant?.tenantId || '';
  const [errorMessage, setErrorMessage] = useState<string>('');

  const { mutate: deleteJob, isPending } = useDeleteEltJob(
    tenantId,
    eltJob.eltJobId,
    {
      onSuccess: () => {
        onClose();
      },
      onError: (error) => {
        setErrorMessage(error);
      },
    }
  );

  const handleDelete = () => {
    setErrorMessage('');
    deleteJob();
  };

  const handleClose = () => {
    if (!isPending) {
      setErrorMessage('');
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="sm">
      <Modal.Header>Delete ELT Job</Modal.Header>
      <Modal.Body>
        <div className="space-y-4">
          {errorMessage && <Alert variant="error">{errorMessage}</Alert>}

          <p className="text-sm text-gray-600">
            Are you sure you want to delete the ELT job{' '}
            <span className="font-semibold">{eltJob.eltJobName}</span>?
          </p>

          <div className="bg-red-50 border border-red-200 rounded-md p-3">
            <p className="text-sm text-red-700">
              This action cannot be undone. All job execution history will be preserved
              but no new executions can be created.
            </p>
          </div>

          <div className="flex items-center justify-end gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={handleClose} disabled={isPending}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDelete} loading={isPending}>
              Delete Job
            </Button>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};
